import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBasket, Sparkles, LayoutDashboard, LogOut, User, ReceiptText, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { warungOpen } from "@/lib/format";
import BranchPicker from "@/components/BranchPicker";

const TIER_BADGE = { Warga: "🥉", Langganan: "🥈", Juragan: "👑" };

export default function Navbar({ search, setSearch, onSearch }) {
  const { user, login, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    const check = () => setIsOpen(warungOpen());
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FBF8F2]/85 border-b border-[#E5DECF]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-2xl bg-emerald-700 text-white flex items-center justify-center font-black text-[11px] shadow-md group-hover:rotate-6 transition">FAS</div>
          <div className="leading-none">
            <div className="font-display font-black text-2xl tracking-tight text-stone-900">FASMART</div>
            <div data-testid="navbar-slogan" className="mt-0.5 flex justify-between w-full text-[7px] font-mono uppercase font-semibold text-emerald-800">
              {"Belanja Dekat, Lebih Hemat.".split("").map((ch, i) => (
                <span key={i}>{ch === " " ? " " : ch}</span>
              ))}
            </div>
          </div>
        </Link>

        <div className="hidden md:flex flex-1 items-center gap-2 max-w-xl mx-4">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="navbar-search-input"
              value={search || ""}
              onChange={(e) => setSearch && setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && onSearch) onSearch(); }}
              placeholder="Ketik nama produk"
              className="w-full pl-9 pr-9 py-2.5 rounded-full border border-[#E5DECF] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50"
            />
            {search && <button data-testid="navbar-search-clear" onClick={() => setSearch && setSearch("")} title="Hapus pencarian" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
          </div>
          {setSearch && (
            <button data-testid="navbar-search-btn" onClick={() => onSearch && onSearch()} className="shrink-0 px-4 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition">Cari</button>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-1 text-xs text-stone-600 font-medium" data-testid="navbar-open-status">
          <span className={`inline-block w-2 h-2 rounded-full animate-pulse ${isOpen ? "bg-emerald-500" : "bg-rose-500"}`}></span>
          <span>{isOpen ? "Buka · Antar 30–60 mnt" : "Tutup · Buka lagi 06.00"}</span>
        </div>

        <BranchPicker />

        <button
          data-testid="navbar-cart-button"
          onClick={() => setOpen(true)}
          className="relative w-11 h-11 rounded-full bg-white border border-[#E5DECF] hover:border-emerald-700 flex items-center justify-center transition"
        >
          <ShoppingBasket className="w-5 h-5 text-stone-800" />
          {count > 0 && (
            <span data-testid="navbar-cart-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
              {count}
            </span>
          )}
        </button>

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="navbar-auth-button" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-[#E5DECF] bg-white hover:border-emerald-700 transition">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-emerald-700 text-white text-xs">{user.name?.[0] || "U"}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-semibold text-stone-800 max-w-[100px] truncate">{user.name?.split(" ")[0]}</span>
                <span data-testid="navbar-tier-badge" title={`Member ${user.tier || "Warga"}`} className="text-sm leading-none">{TIER_BADGE[user.tier] || TIER_BADGE.Warga}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-bold">{user.name}</div>
                <div className="text-xs font-normal text-stone-500">{user.email}</div>
                <div data-testid="navbar-tier-label" className="mt-1 text-[11px] font-mono uppercase tracking-widest text-emerald-700">{TIER_BADGE[user.tier] || TIER_BADGE.Warga} Member {user.tier || "Warga"}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/loyalty")} data-testid="navbar-loyalty-link">
                <Sparkles className="w-4 h-4 mr-2" /> Poin Member
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="navbar-orders-link">
                <ReceiptText className="w-4 h-4 mr-2" /> Pesanan Saya
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="navbar-admin-link">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> {user.input_only ? "📦 Input Produk" : "Admin Dashboard"}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="navbar-logout-btn" className="text-rose-600">
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            data-testid="navbar-login-btn"
            onClick={login}
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5"
          >
            <User className="w-4 h-4 mr-2" /> Masuk
          </Button>
        )}
      </div>
      {setSearch && (
        <div className="md:hidden max-w-7xl mx-auto px-4 pb-3 flex items-center gap-2">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="navbar-search-input-mobile"
              value={search || ""}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && onSearch) onSearch(); }}
              placeholder="Ketik nama produk"
              className="w-full pl-9 pr-9 py-2.5 rounded-full border border-[#E5DECF] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/50"
            />
            {search && <button data-testid="navbar-search-clear-mobile" onClick={() => setSearch("")} title="Hapus pencarian" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 text-stone-500 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition"><X className="w-3 h-3" /></button>}
          </div>
          <button data-testid="navbar-search-btn-mobile" onClick={() => onSearch && onSearch()} className="shrink-0 px-4 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition">Cari</button>
        </div>
      )}
    </header>
  );
}
